/**
 * SettingsPage.jsx - Garage Admin Settings
 * 
 * Allows garage owners to manage:
 * - Garage profile (name, tagline, description)
 * - Cover image and logo
 * - Contact info (phone, address)
 * - Location (latitude/longitude for Google Maps)
 * - Change password
 * - Staff management (add/edit/delete, role-based permissions)
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Spinner } from '../../components/ui/spinner';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { useToast } from '../../hooks/use-toast';
import {
  Settings,
  Store,
  Users,
  MapPin,
  Save,
  UserX,
  Image,
  Phone,
  Mail,
  Globe,
  ExternalLink,
  Upload,
  Loader2,
  Plus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Lock,
  Shield,
  UserPlus,
  Key,
  Crown,
  Printer
} from 'lucide-react';
import SubscriptionTab from './settings/SubscriptionTab';
import WebsiteTab from './settings/WebsiteTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

// Role badge colors
const roleColors = {
  owner: 'bg-purple-100 text-purple-800',
  manager: 'bg-blue-100 text-blue-800',
  supervisor: 'bg-indigo-100 text-indigo-800',
  technician: 'bg-green-100 text-green-800',
  front_desk: 'bg-orange-100 text-orange-800',
  accountant: 'bg-yellow-100 text-yellow-800',
  helper: 'bg-slate-100 text-slate-800'
};

// Role labels in both languages
const roleLabels = {
  en: { owner: 'Owner', manager: 'Manager', supervisor: 'Supervisor', technician: 'Technician', front_desk: 'Front Desk', accountant: 'Accountant', helper: 'Helper' },
  bn: { owner: 'মালিক', manager: 'ম্যানেজার', supervisor: 'সুপারভাইজার', technician: 'টেকনিশিয়ান', front_desk: 'ফ্রন্ট ডেস্ক', accountant: 'হিসাবরক্ষক', helper: 'হেল্পার' }
};

// Available modules for permissions
const availableModules = [
  { key: 'dashboard', label: 'Dashboard', labelBn: 'ড্যাশবোর্ড' },
  { key: 'jobs', label: 'Jobs', labelBn: 'জব' },
  { key: 'customers', label: 'Customers', labelBn: 'গ্রাহক' },
  { key: 'invoices', label: 'Invoices', labelBn: 'চালান' },
  { key: 'inventory', label: 'Inventory', labelBn: 'ইনভেন্টরি' },
  { key: 'services', label: 'Services', labelBn: 'সেবা' },
  { key: 'employees', label: 'Employees', labelBn: 'কর্মচারী', premium: true },
  { key: 'marketing', label: 'Marketing Tools', labelBn: 'মার্কেটিং টুলস', premium: true },
  { key: 'reports', label: 'Reports', labelBn: 'রিপোর্ট', premium: true },
  { key: 'settings', label: 'Settings', labelBn: 'সেটিংস' },
];

const emptyStaffForm = {
  name: '', email: '', phone: '', password: '', role: 'front_desk', permissions: []
};

export default function SettingsPage() {
  const { t, language } = useLanguage();
  const { user, refreshGarageInfo, isPremium } = useAuth();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [users, setUsers] = useState([]);
  
  // Garage settings form state
  const [garageSettings, setGarageSettings] = useState({
    name: '', name_bn: '', phone: '', address: '', address_bn: '',
    description: '', description_bn: '', tagline: '', tagline_bn: '',
    logo_url: '', cover_image_url: '', latitude: '', longitude: '',
    thermal_paper_size: 80
  });
  // Baseline snapshot for dirty-tracking. Updated after every successful save.
  const [garageBaseline, setGarageBaseline] = useState(null);
  // Action surface from WebsiteTab (saveDraft / publish / dirty / busy / preview).
  const [websiteActions, setWebsiteActions] = useState(null);

  // Password change state
  const [passwordForm, setPasswordForm] = useState({ current: '', new: '', confirm: '' });
  const [changingPassword, setChangingPassword] = useState(false);
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);

  // Staff management state
  const [staffDialogOpen, setStaffDialogOpen] = useState(false);
  const [staffForm, setStaffForm] = useState(emptyStaffForm);
  const [editingStaffId, setEditingStaffId] = useState(null);
  const [savingStaff, setSavingStaff] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [showStaffPassword, setShowStaffPassword] = useState(false);
  const [staffFormErrors, setStaffFormErrors] = useState({});
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'profile');

  useEffect(() => {
    const queryTab = searchParams.get('tab');
    // Legacy redirect: 'website' was merged into 'profile' (one source of truth).
    if (queryTab === 'website') {
      const next = new URLSearchParams(searchParams);
      next.delete('tab');
      setSearchParams(next, { replace: true });
      setActiveTab('profile');
      return;
    }
    if (queryTab && queryTab !== activeTab) {
      setActiveTab(queryTab);
    }
  }, [searchParams, activeTab, setSearchParams]);

  const handleTabChange = (nextTab) => {
    setActiveTab(nextTab);
    const nextParams = new URLSearchParams(searchParams);
    if (nextTab === 'profile') {
      nextParams.delete('tab');
    } else {
      nextParams.set('tab', nextTab);
    }
    setSearchParams(nextParams, { replace: true });
  };

  // Fetch data on mount
  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, usersRes] = await Promise.all([
        axios.get(`${API}/settings/garage`),
        axios.get(`${API}/users`)
      ]);
      const data = settingsRes.data;
      const next = {
        name: data.name || '', name_bn: data.name_bn || '', phone: data.phone || '',
        address: data.address || '', address_bn: data.address_bn || '',
        description: data.description || '', description_bn: data.description_bn || '',
        tagline: data.tagline || '', tagline_bn: data.tagline_bn || '',
        logo_url: data.logo_url || '', cover_image_url: data.cover_image_url || '',
        latitude: data.latitude || '', longitude: data.longitude || '',
        thermal_paper_size: data.thermal_paper_size || 80
      };
      setGarageSettings(next);
      setGarageBaseline(JSON.stringify(next));
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast({ title: language === 'en' ? 'Failed to load settings' : 'সেটিংস লোড ব্যর্থ', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // Save garage settings
  const saveGarageSettings = async () => {
    setSaving(true);
    try {
      const payload = {};
      Object.entries(garageSettings).forEach(([key, value]) => {
        if (value !== '' && value !== null) {
          if (key === 'latitude' || key === 'longitude') {
            payload[key] = parseFloat(value) || null;
          } else if (key === 'thermal_paper_size') {
            payload[key] = parseInt(value) || 80;
          } else {
            payload[key] = value;
          }
        }
      });
      await axios.put(`${API}/settings/garage`, payload);
      setGarageBaseline(JSON.stringify(garageSettings));
      toast({ title: language === 'en' ? 'Settings saved!' : 'সেটিংস সংরক্ষিত!' });
      // Refresh the cached garage info in AuthContext
      if (refreshGarageInfo) refreshGarageInfo();
    } catch (error) {
      toast({ title: language === 'en' ? 'Failed to save' : 'সংরক্ষণ ব্যর্থ', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  // Handle file upload
  const handleImageUpload = async (event, imageType) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      toast({ title: language === 'en' ? 'Invalid file type' : 'অবৈধ ফাইল টাইপ', variant: 'destructive' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: language === 'en' ? 'File too large (max 5MB)' : 'ফাইল অনেক বড় (সর্বোচ্চ ৫MB)', variant: 'destructive' });
      return;
    }
    const setUploading = imageType === 'logo' ? setUploadingLogo : setUploadingCover;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/upload/image?image_type=${imageType}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const urlField = imageType === 'logo' ? 'logo_url' : 'cover_image_url';
      setGarageSettings(prev => ({ ...prev, [urlField]: response.data.url }));
      toast({ title: language === 'en' ? 'Image uploaded!' : 'ছবি আপলোড হয়েছে!' });
    } catch (error) {
      toast({ title: language === 'en' ? 'Upload failed' : 'আপলোড ব্যর্থ', variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  // Change password
  const handleChangePassword = async () => {
    if (!passwordForm.current || !passwordForm.new) return;
    if (passwordForm.new.length < 6) {
      toast({ title: language === 'en' ? 'New password must be at least 6 characters' : 'নতুন পাসওয়ার্ড কমপক্ষে ৬ অক্ষর হতে হবে', variant: 'destructive' });
      return;
    }
    if (passwordForm.new !== passwordForm.confirm) {
      toast({ title: language === 'en' ? 'Passwords do not match' : 'পাসওয়ার্ড মিলছে না', variant: 'destructive' });
      return;
    }
    setChangingPassword(true);
    try {
      await axios.put(`${API}/auth/change-password`, {
        current_password: passwordForm.current,
        new_password: passwordForm.new
      });
      toast({ title: language === 'en' ? 'Password changed successfully!' : 'পাসওয়ার্ড সফলভাবে পরিবর্তিত!' });
      setPasswordForm({ current: '', new: '', confirm: '' });
    } catch (error) {
      const msg = error.response?.data?.detail || (language === 'en' ? 'Failed to change password' : 'পাসওয়ার্ড পরিবর্তন ব্যর্থ');
      toast({ title: msg, variant: 'destructive' });
    } finally {
      setChangingPassword(false);
    }
  };

  // -- Staff CRUD --
  const openAddStaff = () => {
    setEditingStaffId(null);
    setStaffForm(emptyStaffForm);
    setStaffFormErrors({});
    setShowStaffPassword(false);
    setStaffDialogOpen(true);
  };

  const openEditStaff = (staffUser) => {
    setEditingStaffId(staffUser.id);
    setStaffForm({
      name: staffUser.name || '',
      email: staffUser.email || '',
      phone: staffUser.phone || '',
      password: '',
      role: staffUser.role || 'front_desk',
      permissions: staffUser.permissions || []
    });
    setStaffFormErrors({});
    setShowStaffPassword(false);
    setStaffDialogOpen(true);
  };

  const validateStaffForm = () => {
    const errors = {};
    if (!staffForm.name.trim()) errors.name = true;
    if (!staffForm.email.trim()) errors.email = true;
    if (!staffForm.phone.trim() || !/^01\d{9}$/.test(staffForm.phone.trim())) errors.phone = true;
    if (!editingStaffId && (!staffForm.password || staffForm.password.length < 6)) errors.password = true;
    if (editingStaffId && staffForm.password && staffForm.password.length < 6) errors.password = true;
    setStaffFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const saveStaff = async () => {
    if (!validateStaffForm()) return;
    setSavingStaff(true);
    try {
      if (editingStaffId) {
        const payload = {
          name: staffForm.name,
          email: staffForm.email,
          phone: staffForm.phone,
          role: staffForm.role,
          permissions: staffForm.permissions,
        };
        if (staffForm.password) payload.password = staffForm.password;
        await axios.put(`${API}/users/${editingStaffId}`, payload);
        toast({ title: language === 'en' ? 'Staff updated!' : 'স্টাফ আপডেট হয়েছে!' });
      } else {
        await axios.post(`${API}/users`, {
          name: staffForm.name,
          email: staffForm.email,
          phone: staffForm.phone,
          password: staffForm.password,
          role: staffForm.role,
        });
        toast({ title: language === 'en' ? 'Staff added!' : 'স্টাফ যোগ হয়েছে!' });
      }
      setStaffDialogOpen(false);
      fetchData();
    } catch (error) {
      const msg = error.response?.data?.detail || (language === 'en' ? 'Operation failed' : 'অপারেশন ব্যর্থ');
      toast({ title: msg, variant: 'destructive' });
    } finally {
      setSavingStaff(false);
    }
  };

  const deleteStaff = async (userId) => {
    try {
      await axios.delete(`${API}/users/${userId}`);
      toast({ title: language === 'en' ? 'Staff removed!' : 'স্টাফ মুছে ফেলা হয়েছে!' });
      setDeleteConfirmId(null);
      fetchData();
    } catch (error) {
      const msg = error.response?.data?.detail || (language === 'en' ? 'Failed to delete' : 'মুছে ফেলা ব্যর্থ');
      toast({ title: msg, variant: 'destructive' });
    }
  };

  const toggleUserActive = async (userId) => {
    try {
      await axios.put(`${API}/users/${userId}/toggle-active`);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_active: !u.is_active } : u));
      toast({ title: language === 'en' ? 'Status updated!' : 'স্ট্যাটাস আপডেট!' });
    } catch (error) {
      toast({ title: language === 'en' ? 'Failed to update' : 'আপডেট ব্যর্থ', variant: 'destructive' });
    }
  };

  const togglePermission = (moduleKey) => {
    setStaffForm(prev => {
      const perms = prev.permissions || [];
      if (perms.includes(moduleKey)) {
        return { ...prev, permissions: perms.filter(p => p !== moduleKey) };
      } else {
        return { ...prev, permissions: [...perms, moduleKey] };
      }
    });
  };

  // Get current location from browser
  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setGarageSettings(prev => ({
            ...prev,
            latitude: position.coords.latitude.toFixed(6),
            longitude: position.coords.longitude.toFixed(6)
          }));
          toast({ title: language === 'en' ? 'Location detected!' : 'অবস্থান শনাক্ত হয়েছে!' });
        },
        () => {
          toast({ title: language === 'en' ? 'Could not get location' : 'অবস্থান পাওয়া যায়নি', variant: 'destructive' });
        }
      );
    }
  };

  // Preview garage page
  const previewGaragePage = () => {
    const slug = user?.tenant_slug;
    if (slug) window.open(`/${slug}`, '_blank');
  };

  // Image upload bumps the dirty flag because logo_url/cover_image_url change.
  // Note: server-side the URL is already persisted at upload time, but the
  // tenant doc still needs Save to attach it. We treat the field change as dirty.

  const isOwner = user?.role === 'owner';
  const garageDirty = useMemo(
    () => garageBaseline !== null && JSON.stringify(garageSettings) !== garageBaseline,
    [garageSettings, garageBaseline]
  );
  const tenantSlug = user?.tenant_slug;
  const previewHref = tenantSlug ? `/${tenantSlug}` : null;

  // Stable callback so WebsiteTab's effect doesn't churn the parent.
  const handleWebsiteActions = useCallback((actions) => {
    setWebsiteActions(actions);
  }, []);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <Spinner size="lg" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6 pb-28">
        {/* Page Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Barlow_Condensed']">
              {language === 'en' ? 'Settings' : 'সেটিংস'}
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              {language === 'en' ? 'Manage your garage profile, staff, and security' : 'আপনার গ্যারেজ প্রোফাইল, স্টাফ এবং নিরাপত্তা পরিচালনা করুন'}
            </p>
          </div>
        </div>

        {/* Settings Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
          <TabsList className="grid w-full grid-cols-5 lg:w-[680px]">
            <TabsTrigger value="profile" className="gap-2">
              <Globe className="w-4 h-4" />
              <span className="hidden sm:inline">{language === 'en' ? 'Garage & Site' : 'গ্যারেজ ও সাইট'}</span>
            </TabsTrigger>
            <TabsTrigger value="location" className="gap-2">
              <MapPin className="w-4 h-4" />
              <span className="hidden sm:inline">{language === 'en' ? 'Location' : 'অবস্থান'}</span>
            </TabsTrigger>
            <TabsTrigger value="staff" className="gap-2">
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">{language === 'en' ? 'Staff' : 'স্টাফ'}</span>
            </TabsTrigger>
            <TabsTrigger value="security" className="gap-2">
              <Lock className="w-4 h-4" />
              <span className="hidden sm:inline">{language === 'en' ? 'Security' : 'নিরাপত্তা'}</span>
            </TabsTrigger>
            <TabsTrigger value="subscription" className="gap-2">
              <Crown className="w-4 h-4" />
              <span className="hidden sm:inline">{language === 'en' ? 'Subscription' : 'সাবস্ক্রিপশন'}</span>
            </TabsTrigger>
          </TabsList>

          {/* ========== PROFILE TAB ========== */}
          <TabsContent value="profile">
            <div className="grid gap-6 md:grid-cols-2">
              {/* Basic Info Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Store className="w-5 h-5 text-blue-600" />
                    {language === 'en' ? 'Basic Info' : 'মৌলিক তথ্য'}
                  </CardTitle>
                  <CardDescription>
                    {language === 'en' ? 'Your garage name and contact details' : 'আপনার গ্যারেজের নাম এবং যোগাযোগের বিবরণ'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>{language === 'en' ? 'Name (English)' : 'নাম (ইংরেজি)'}</Label>
                      <Input value={garageSettings.name} onChange={(e) => setGarageSettings(p => ({ ...p, name: e.target.value }))} data-testid="garage-name" />
                    </div>
                    <div>
                      <Label>{language === 'en' ? 'Name (Bengali)' : 'নাম (বাংলা)'}</Label>
                      <Input value={garageSettings.name_bn} onChange={(e) => setGarageSettings(p => ({ ...p, name_bn: e.target.value }))} />
                    </div>
                  </div>
                  <div>
                    <Label className="flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      {language === 'en' ? 'Phone' : 'ফোন'}
                    </Label>
                    <Input value={garageSettings.phone} onChange={(e) => setGarageSettings(p => ({ ...p, phone: e.target.value }))} placeholder="01XXXXXXXXX" data-testid="garage-phone" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>{language === 'en' ? 'Tagline (English)' : 'ট্যাগলাইন (ইংরেজি)'}</Label>
                      <Input value={garageSettings.tagline} onChange={(e) => setGarageSettings(p => ({ ...p, tagline: e.target.value }))} placeholder="Your trusted bike service partner" data-testid="garage-tagline" />
                    </div>
                    <div>
                      <Label>{language === 'en' ? 'Tagline (Bengali)' : 'ট্যাগলাইন (বাংলা)'}</Label>
                      <Input value={garageSettings.tagline_bn} onChange={(e) => setGarageSettings(p => ({ ...p, tagline_bn: e.target.value }))} placeholder="আপনার বিশ্বস্ত বাইক সার্ভিস পার্টনার" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Description Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="w-5 h-5 text-blue-600" />
                    {language === 'en' ? 'About Your Garage' : 'আপনার গ্যারেজ সম্পর্কে'}
                  </CardTitle>
                  <CardDescription>
                    {language === 'en' ? 'Description shown on your public page' : 'আপনার পাবলিক পেজে দেখানো বিবরণ'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>{language === 'en' ? 'Description (English)' : 'বিবরণ (ইংরেজি)'}</Label>
                    <Textarea value={garageSettings.description} onChange={(e) => setGarageSettings(p => ({ ...p, description: e.target.value }))} rows={3} placeholder="Tell customers about your garage, services, and expertise..." data-testid="garage-description" />
                  </div>
                  <div>
                    <Label>{language === 'en' ? 'Description (Bengali)' : 'বিবরণ (বাংলা)'}</Label>
                    <Textarea value={garageSettings.description_bn} onChange={(e) => setGarageSettings(p => ({ ...p, description_bn: e.target.value }))} rows={3} placeholder="গ্রাহকদের আপনার গ্যারেজ, সেবা এবং দক্ষতা সম্পর্কে বলুন..." />
                  </div>
                </CardContent>
              </Card>

              {/* Images Card */}
              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Image className="w-5 h-5 text-blue-600" />
                    {language === 'en' ? 'Images' : 'ছবি'}
                  </CardTitle>
                  <CardDescription>
                    {language === 'en' ? 'Logo and cover image for your public page' : 'আপনার পাবলিক পেজের জন্য লোগো এবং কভার ইমেজ'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Logo Upload */}
                    <div>
                      <Label>{language === 'en' ? 'Logo' : 'লোগো'}</Label>
                      <div className="mt-2 flex items-start gap-4">
                        {garageSettings.logo_url ? (
                          <img src={garageSettings.logo_url} alt="Logo" className="w-20 h-20 object-cover rounded-lg border" />
                        ) : (
                          <div className="w-20 h-20 bg-slate-100 rounded-lg flex items-center justify-center border">
                            <Image className="w-8 h-8 text-slate-400" />
                          </div>
                        )}
                        <div className="flex-1">
                          <input type="file" id="logo-upload" className="hidden" accept="image/jpeg,image/png,image/webp,image/gif" onChange={(e) => handleImageUpload(e, 'logo')} />
                          <label htmlFor="logo-upload">
                            <Button variant="outline" className="gap-2 cursor-pointer" disabled={uploadingLogo} asChild>
                              <span>{uploadingLogo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}{language === 'en' ? 'Upload Logo' : 'লোগো আপলোড'}</span>
                            </Button>
                          </label>
                          <p className="text-xs text-slate-500 mt-2">{language === 'en' ? 'Square image, max 5MB' : 'বর্গাকার ছবি, সর্বোচ্চ ৫MB'}</p>
                        </div>
                      </div>
                    </div>
                    {/* Cover Image Upload */}
                    <div>
                      <Label>{language === 'en' ? 'Cover Image' : 'কভার ইমেজ'}</Label>
                      <div className="mt-2">
                        {garageSettings.cover_image_url ? (
                          <img src={garageSettings.cover_image_url} alt="Cover" className="w-full h-32 object-cover rounded-lg border mb-2" />
                        ) : (
                          <div className="w-full h-32 bg-slate-100 rounded-lg flex items-center justify-center border mb-2">
                            <Image className="w-12 h-12 text-slate-400" />
                          </div>
                        )}
                        <input type="file" id="cover-upload" className="hidden" accept="image/jpeg,image/png,image/webp,image/gif" onChange={(e) => handleImageUpload(e, 'cover')} />
                        <label htmlFor="cover-upload">
                          <Button variant="outline" className="gap-2 cursor-pointer" disabled={uploadingCover} asChild>
                            <span>{uploadingCover ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}{language === 'en' ? 'Upload Cover' : 'কভার আপলোড'}</span>
                          </Button>
                        </label>
                        <p className="text-xs text-slate-500 mt-2">{language === 'en' ? 'Recommended: 1920x1080px, max 5MB' : 'প্রস্তাবিত: ১৯২০x১০৮০px, সর্বোচ্চ ৫MB'}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Website builder moved to its own /admin/website hub — kept here as
                a reference link to avoid the double surface. */}
          </TabsContent>

          {/* ========== LOCATION TAB ========== */}
          <TabsContent value="location">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-blue-600" />
                  {language === 'en' ? 'Location & Address' : 'অবস্থান ও ঠিকানা'}
                </CardTitle>
                <CardDescription>
                  {language === 'en' ? 'Your address and GPS coordinates for Google Maps directions' : 'গুগল ম্যাপস দিকনির্দেশনার জন্য আপনার ঠিকানা এবং GPS স্থানাঙ্ক'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>{language === 'en' ? 'Address (English)' : 'ঠিকানা (ইংরেজি)'}</Label>
                    <Textarea value={garageSettings.address} onChange={(e) => setGarageSettings(p => ({ ...p, address: e.target.value }))} rows={2} placeholder="123 Main Road, Dhanmondi, Dhaka-1205" data-testid="garage-address" />
                  </div>
                  <div>
                    <Label>{language === 'en' ? 'Address (Bengali)' : 'ঠিকানা (বাংলা)'}</Label>
                    <Textarea value={garageSettings.address_bn} onChange={(e) => setGarageSettings(p => ({ ...p, address_bn: e.target.value }))} rows={2} placeholder="১২৩ মেইন রোড, ধানমন্ডি, ঢাকা-১২০৫" />
                  </div>
                </div>
                <div className="border-t pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h4 className="font-medium">{language === 'en' ? 'GPS Coordinates' : 'GPS স্থানাঙ্ক'}</h4>
                      <p className="text-sm text-slate-500">{language === 'en' ? 'Used for "Get Directions" button on your page' : 'আপনার পেজে "দিকনির্দেশনা পান" বাটনের জন্য ব্যবহৃত'}</p>
                    </div>
                    <Button variant="outline" onClick={getCurrentLocation} className="gap-2" data-testid="get-location">
                      <MapPin className="w-4 h-4" />
                      {language === 'en' ? 'Get Current Location' : 'বর্তমান অবস্থান পান'}
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>{language === 'en' ? 'Latitude' : 'অক্ষাংশ'}</Label>
                      <Input value={garageSettings.latitude} onChange={(e) => setGarageSettings(p => ({ ...p, latitude: e.target.value }))} placeholder="23.7465" type="number" step="0.000001" data-testid="garage-lat" />
                    </div>
                    <div>
                      <Label>{language === 'en' ? 'Longitude' : 'দ্রাঘিমাংশ'}</Label>
                      <Input value={garageSettings.longitude} onChange={(e) => setGarageSettings(p => ({ ...p, longitude: e.target.value }))} placeholder="90.3765" type="number" step="0.000001" data-testid="garage-lng" />
                    </div>
                  </div>
                  {garageSettings.latitude && garageSettings.longitude && (
                    <div className="mt-4">
                      <a href={`https://www.google.com/maps?q=${garageSettings.latitude},${garageSettings.longitude}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm flex items-center gap-1">
                        <ExternalLink className="w-3 h-3" />
                        {language === 'en' ? 'Preview on Google Maps' : 'গুগল ম্যাপসে দেখুন'}
                      </a>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ========== STAFF TAB ========== */}
          <TabsContent value="staff">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="w-5 h-5 text-blue-600" />
                      {language === 'en' ? 'Staff Members' : 'স্টাফ সদস্য'}
                    </CardTitle>
                    <CardDescription>
                      {language === 'en' ? 'Manage staff access, roles, and module permissions' : 'স্টাফ অ্যাক্সেস, ভূমিকা এবং মডিউল অনুমতি পরিচালনা করুন'}
                    </CardDescription>
                  </div>
                  {isOwner && (
                    <Button onClick={openAddStaff} className="gap-2" data-testid="add-staff-btn">
                      <UserPlus className="w-4 h-4" />
                      {language === 'en' ? 'Add Staff' : 'স্টাফ যোগ করুন'}
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {users.length === 0 ? (
                    <p className="text-slate-500 text-center py-8">
                      {language === 'en' ? 'No staff members yet' : 'এখনও কোনো স্টাফ সদস্য নেই'}
                    </p>
                  ) : (
                    users.map((staffUser) => (
                      <div key={staffUser.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border rounded-lg gap-3">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center shrink-0">
                            <span className="text-lg font-bold text-slate-600">
                              {staffUser.name?.charAt(0)?.toUpperCase() || 'U'}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-slate-900">{staffUser.name}</p>
                            <p className="text-sm text-slate-500 truncate">{staffUser.email}</p>
                            {staffUser.phone && <p className="text-xs text-slate-400">{staffUser.phone}</p>}
                            {staffUser.permissions && staffUser.permissions.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {staffUser.permissions.map(p => (
                                  <span key={p} className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded">{p}</span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={roleColors[staffUser.role] || 'bg-slate-100'}>
                            {roleLabels[language]?.[staffUser.role] || staffUser.role}
                          </Badge>
                          {!staffUser.is_active && (
                            <Badge variant="outline" className="text-red-500 border-red-300">
                              {language === 'en' ? 'Inactive' : 'নিষ্ক্রিয়'}
                            </Badge>
                          )}
                          {staffUser.role !== 'owner' && isOwner && (
                            <>
                              <Button variant="outline" size="sm" className="gap-1" onClick={() => openEditStaff(staffUser)} data-testid={`edit-user-${staffUser.id}`}>
                                <Edit className="w-3 h-3" />
                                {language === 'en' ? 'Edit' : 'সম্পাদনা'}
                              </Button>
                              <Button
                                variant={staffUser.is_active ? 'outline' : 'default'}
                                size="sm"
                                onClick={() => toggleUserActive(staffUser.id)}
                                data-testid={`toggle-user-${staffUser.id}`}
                              >
                                {staffUser.is_active ? <UserX className="w-3 h-3" /> : (language === 'en' ? 'Activate' : 'সক্রিয়')}
                              </Button>
                              <Button variant="destructive" size="sm" className="gap-1" onClick={() => setDeleteConfirmId(staffUser.id)} data-testid={`delete-user-${staffUser.id}`}>
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ========== SECURITY TAB ========== */}
          <TabsContent value="security">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Key className="w-5 h-5 text-blue-600" />
                  {language === 'en' ? 'Change Password' : 'পাসওয়ার্ড পরিবর্তন করুন'}
                </CardTitle>
                <CardDescription>
                  {language === 'en' ? 'Update your account login password' : 'আপনার অ্যাকাউন্ট লগইন পাসওয়ার্ড আপডেট করুন'}
                </CardDescription>
              </CardHeader>
              <CardContent className="max-w-md space-y-4">
                <div>
                  <Label>{language === 'en' ? 'Current Password' : 'বর্তমান পাসওয়ার্ড'} <span className="text-red-500">*</span></Label>
                  <div className="relative">
                    <Input
                      type={showCurrentPw ? 'text' : 'password'}
                      value={passwordForm.current}
                      onChange={(e) => setPasswordForm(p => ({ ...p, current: e.target.value }))}
                      className="pr-10"
                      data-testid="current-password"
                    />
                    <button type="button" onClick={() => setShowCurrentPw(!showCurrentPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      {showCurrentPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label>{language === 'en' ? 'New Password' : 'নতুন পাসওয়ার্ড'} <span className="text-red-500">*</span></Label>
                  <div className="relative">
                    <Input
                      type={showNewPw ? 'text' : 'password'}
                      value={passwordForm.new}
                      onChange={(e) => setPasswordForm(p => ({ ...p, new: e.target.value }))}
                      className="pr-10"
                      data-testid="new-password"
                    />
                    <button type="button" onClick={() => setShowNewPw(!showNewPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      {showNewPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{language === 'en' ? 'Minimum 6 characters' : 'ন্যূনতম ৬ অক্ষর'}</p>
                </div>
                <div>
                  <Label>{language === 'en' ? 'Confirm New Password' : 'নতুন পাসওয়ার্ড নিশ্চিত করুন'} <span className="text-red-500">*</span></Label>
                  <Input
                    type="password"
                    value={passwordForm.confirm}
                    onChange={(e) => setPasswordForm(p => ({ ...p, confirm: e.target.value }))}
                    data-testid="confirm-password"
                  />
                  {passwordForm.confirm && passwordForm.new !== passwordForm.confirm && (
                    <p className="text-xs text-red-500 mt-1">{language === 'en' ? 'Passwords do not match' : 'পাসওয়ার্ড মিলছে না'}</p>
                  )}
                </div>
                <Button
                  onClick={handleChangePassword}
                  disabled={changingPassword || !passwordForm.current || !passwordForm.new || !passwordForm.confirm}
                  className="gap-2"
                  data-testid="change-password-btn"
                >
                  {changingPassword ? <Spinner size="sm" className="border-white" /> : <Lock className="w-4 h-4" />}
                  {language === 'en' ? 'Change Password' : 'পাসওয়ার্ড পরিবর্তন করুন'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ========== SUBSCRIPTION TAB ========== */}
          <TabsContent value="subscription">
            <SubscriptionTab />
          </TabsContent>
        </Tabs>
      </div>

      {/* ========== ADD/EDIT STAFF DIALOG ========== */}
      <Dialog open={staffDialogOpen} onOpenChange={setStaffDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingStaffId
                ? (language === 'en' ? 'Edit Staff Member' : 'স্টাফ সদস্য সম্পাদনা')
                : (language === 'en' ? 'Add Staff Member' : 'স্টাফ সদস্য যোগ করুন')}
            </DialogTitle>
            <DialogDescription>
              {language === 'en' ? 'Set login credentials, role, and module permissions' : 'লগইন তথ্য, ভূমিকা এবং মডিউল অনুমতি সেট করুন'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Name */}
            <div>
              <Label>{language === 'en' ? 'Name' : 'নাম'} <span className="text-red-500">*</span></Label>
              <Input
                value={staffForm.name}
                onChange={(e) => { setStaffForm(p => ({ ...p, name: e.target.value })); setStaffFormErrors(p => ({ ...p, name: false })); }}
                className={staffFormErrors.name ? 'border-red-500' : ''}
                data-testid="staff-name"
              />
            </div>
            {/* Email */}
            <div>
              <Label>{language === 'en' ? 'Email (Login ID)' : 'ইমেইল (লগইন আইডি)'} <span className="text-red-500">*</span></Label>
              <Input
                type="email"
                value={staffForm.email}
                onChange={(e) => { setStaffForm(p => ({ ...p, email: e.target.value })); setStaffFormErrors(p => ({ ...p, email: false })); }}
                className={staffFormErrors.email ? 'border-red-500' : ''}
                data-testid="staff-email"
              />
            </div>
            {/* Phone */}
            <div>
              <Label>{language === 'en' ? 'Phone' : 'ফোন'} <span className="text-red-500">*</span></Label>
              <Input
                type="tel"
                maxLength={11}
                value={staffForm.phone}
                onChange={(e) => { const v = e.target.value.replace(/\D/g, '').slice(0, 11); setStaffForm(p => ({ ...p, phone: v })); setStaffFormErrors(p => ({ ...p, phone: false })); }}
                placeholder="01XXXXXXXXX"
                className={staffFormErrors.phone ? 'border-red-500' : ''}
                data-testid="staff-phone"
              />
            </div>
            {/* Password */}
            <div>
              <Label>
                {language === 'en' ? 'Password' : 'পাসওয়ার্ড'} {!editingStaffId && <span className="text-red-500">*</span>}
              </Label>
              <div className="relative">
                <Input
                  type={showStaffPassword ? 'text' : 'password'}
                  value={staffForm.password}
                  onChange={(e) => { setStaffForm(p => ({ ...p, password: e.target.value })); setStaffFormErrors(p => ({ ...p, password: false })); }}
                  placeholder={editingStaffId ? (language === 'en' ? 'Leave empty to keep current' : 'পরিবর্তন না করলে খালি রাখুন') : ''}
                  className={`pr-10 ${staffFormErrors.password ? 'border-red-500' : ''}`}
                  data-testid="staff-password"
                />
                <button type="button" onClick={() => setShowStaffPassword(!showStaffPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showStaffPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-1">{language === 'en' ? 'Min 6 characters' : 'ন্যূনতম ৬ অক্ষর'}</p>
            </div>
            {/* Role */}
            <div>
              <Label>{language === 'en' ? 'Role' : 'ভূমিকা'}</Label>
              <Select value={staffForm.role} onValueChange={(v) => setStaffForm(p => ({ ...p, role: v }))}>
                <SelectTrigger data-testid="staff-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manager">{language === 'en' ? 'Manager' : 'ম্যানেজার'}</SelectItem>
                  <SelectItem value="supervisor">{language === 'en' ? 'Supervisor' : 'সুপারভাইজার'}</SelectItem>
                  <SelectItem value="front_desk">{language === 'en' ? 'Front Desk' : 'ফ্রন্ট ডেস্ক'}</SelectItem>
                  <SelectItem value="technician">{language === 'en' ? 'Technician' : 'টেকনিশিয়ান'}</SelectItem>
                  <SelectItem value="accountant">{language === 'en' ? 'Accountant' : 'হিসাবরক্ষক'}</SelectItem>
                  <SelectItem value="helper">{language === 'en' ? 'Helper' : 'হেল্পার'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* Module Permissions */}
            <div>
              <Label className="flex items-center gap-2 mb-2">
                <Shield className="w-4 h-4" />
                {language === 'en' ? 'Module Permissions' : 'মডিউল অনুমতি'}
              </Label>
              <p className="text-xs text-slate-500 mb-3">
                {language === 'en' ? 'Select which modules this staff member can access' : 'এই স্টাফ সদস্য কোন মডিউলগুলোতে অ্যাক্সেস পাবে নির্বাচন করুন'}
              </p>
              <div className="grid grid-cols-2 gap-3">
                {availableModules.map((mod) => {
                  const isPremiumModule = mod.premium && !isPremium;
                  return (
                    <label key={mod.key} className={`flex items-center gap-2 p-2 border rounded-lg cursor-pointer ${isPremiumModule ? 'opacity-50 bg-slate-50' : 'hover:bg-slate-50'}`}>
                      <Checkbox
                        checked={(staffForm.permissions || []).includes(mod.key)}
                        onCheckedChange={() => !isPremiumModule && togglePermission(mod.key)}
                        disabled={isPremiumModule}
                        data-testid={`perm-${mod.key}`}
                      />
                      <span className="text-sm flex items-center gap-1">
                        {language === 'bn' ? mod.labelBn : mod.label}
                        {isPremiumModule && <Crown className="w-3 h-3 text-emerald-600" />}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setStaffDialogOpen(false)}>
              {language === 'en' ? 'Cancel' : 'বাতিল'}
            </Button>
            <Button onClick={saveStaff} disabled={savingStaff} className="gap-2">
              {savingStaff ? <Spinner size="sm" className="border-white" /> : <Save className="w-4 h-4" />}
              {editingStaffId ? (language === 'en' ? 'Save Changes' : 'সংরক্ষণ') : (language === 'en' ? 'Add Staff' : 'যোগ করুন')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========== DELETE CONFIRM DIALOG ========== */}
      <AlertDialog open={!!deleteConfirmId} onOpenChange={(open) => { if (!open) setDeleteConfirmId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {language === 'en' ? 'Delete Staff Member?' : 'স্টাফ সদস্য মুছে ফেলবেন?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {language === 'en'
                ? 'This will permanently remove this staff member and their login access. This action cannot be undone.'
                : 'এটি স্থায়ীভাবে এই স্টাফ সদস্য এবং তার লগইন অ্যাক্সেস মুছে ফেলবে। এই কাজটি পূর্বাবস্থায় ফেরানো যাবে না।'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{language === 'en' ? 'Cancel' : 'বাতিল'}</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => deleteStaff(deleteConfirmId)}>
              {language === 'en' ? 'Delete' : 'মুছে ফেলুন'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ========== UNIFIED FLOATING ACTION BAR ==========
          One cohesive surface for Preview / Save / Save draft / Publish across
          tabs. Save buttons disable when nothing is dirty. */}
      {(activeTab === 'profile' || activeTab === 'location') && (
        <div className="fixed bottom-16 md:bottom-0 left-0 right-0 md:left-[var(--admin-sidebar-w,0px)] z-30 pointer-events-none">
          <div className="max-w-5xl mx-auto px-3 md:px-4 pb-3 md:pb-4 pointer-events-auto">
            <div className="rounded-2xl border border-slate-200 bg-white/95 backdrop-blur-md shadow-[0_18px_50px_-20px_rgba(15,23,42,0.25)] flex flex-col sm:flex-row items-stretch sm:items-center sm:justify-between gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3">
              <div className="flex items-center gap-2 text-xs text-slate-500 min-w-0">
                {garageDirty || websiteActions?.dirty ? (
                  <span className="inline-flex items-center gap-1.5 text-amber-700 font-medium">
                    <span className="inline-block w-2 h-2 rounded-full bg-amber-500" />
                    {language === 'en' ? 'Unsaved changes' : 'অসংরক্ষিত পরিবর্তন'}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-emerald-700 font-medium">
                    <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
                    {language === 'en' ? 'All changes saved' : 'সমস্ত পরিবর্তন সংরক্ষিত'}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap justify-end">
                {previewHref && (
                  <Button asChild variant="outline" size="sm" className="gap-1.5">
                    <a href={previewHref} target="_blank" rel="noopener noreferrer">
                      <Eye className="w-4 h-4" />
                      <span className="hidden sm:inline">{language === 'en' ? 'Preview' : 'প্রিভিউ'}</span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </Button>
                )}
                <Button
                  onClick={saveGarageSettings}
                  disabled={saving || !garageDirty}
                  size="sm"
                  variant="outline"
                  className="gap-1.5"
                  data-testid="save-profile"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {language === 'en' ? 'Save' : 'সংরক্ষণ'}
                </Button>
                {activeTab === 'profile' && websiteActions && (
                  <>
                    <Button
                      onClick={() => websiteActions.saveDraft?.()}
                      disabled={websiteActions.saving || !websiteActions.dirty}
                      size="sm"
                      variant="outline"
                      className="gap-1.5"
                    >
                      {websiteActions.saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      {language === 'en' ? 'Save draft' : 'ড্রাফট'}
                    </Button>
                    <Button
                      onClick={() => websiteActions.publish?.()}
                      disabled={websiteActions.publishing}
                      size="sm"
                      className="gap-1.5 bg-emerald-600 hover:bg-emerald-700"
                    >
                      {websiteActions.publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Globe className="w-4 h-4" />}
                      {language === 'en' ? 'Publish' : 'প্রকাশ'}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
